/** Shared fitness-plan types (no domain / persistence naming). */

export type FitnessScheduleKey =
  | 'sun'
  | 'mon'
  | 'tue'
  | 'wed'
  | 'thu'
  | 'fri'
  | 'sat';

export const FITNESS_SCHEDULE_KEYS: readonly FitnessScheduleKey[] = [
  'sun',
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
] as const;

/** Per-set prescription; rep range is inclusive on both ends. */
export interface FitnessPlanSet {
  repRangeMin: number;
  repRangeMax: number;
  targetLoadKg: number;
  restSec: number;
  notes?: string;
}

export interface ExerciseRationaleGoal {
  heading: string;
  text: string;
}

/** Structured exercise rationale returned by the API. */
export interface ExerciseRationale {
  order: string;
  choice: string;
  prescription: string;
  goals: ExerciseRationaleGoal[];
}

export interface FitnessPlanExercise {
  exerciseId: string;
  exerciseName: string;
  /** @deprecated legacy field; prefer ``exerciseName``. */
  name?: string;
  equipmentId?: string;
  order: number;
  /** Flattened rationale for coach prompts; prefer ``rationale`` in the editor. */
  notes?: string;
  rationale?: ExerciseRationale;
  sets: FitnessPlanSet[];
}

export interface FitnessPlanWorkout {
  workoutId: string;
  /** Primary display name (mirrors legacy ``title`` when loaded from older docs). */
  name: string;
  /** Legacy mirror of ``name`` for older clients; optional on write. */
  title?: string;
  description?: string;
  notes?: string;
  order: number;
  createdAt?: string | null;
  updatedAt?: string | null;
  exercises: FitnessPlanExercise[];
}

/**
 * A plan's goal: exactly one Primary Goal plus one or more Goal Selections
 * (`Category → optional Subcategory → optional Focus Tag`). Selections are
 * stored in insertion order and are unique by `(category, subcategory, focusTag)`.
 */
export interface FitnessPlanGoalSelection {
  category: string;
  subcategory?: string;
  focusTag?: string;
}

export interface FitnessPlanGoal {
  primary: string;
  selections: FitnessPlanGoalSelection[];
}

export interface FitnessPlanSummary {
  planId: string;
  name: string;
  goal?: FitnessPlanGoal;
  updatedAt?: string | null;
}

export type FitnessPlanSessionStatus =
  | 'in_progress'
  | 'completed'
  | 'discarded';
